// AzureOpenAIService.js - FIXED VERSION with Animation Overlap Prevention
class AzureOpenAIService {
  constructor(endpoint, apiKey, deployment) {
    this.endpoint = endpoint;
    this.apiKey = apiKey;
    this.deployment = deployment;
    
    // Request management
    this.requestQueue = [];
    this.isProcessingQueue = false;
    this.lastRequestTime = 0;
    this.minRequestInterval = 1000; // 1 second between requests
    this.maxRetries = 3;
    
    // FIXED: Track active requests to prevent duplicates
    this.activeRequests = new Map();
    this.requestTimeout = 30000; // 30 seconds timeout
    
    // Cache for avoiding duplicate requests - DISABLED for summaries
    this.requestCache = new Map();
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
  }
  
  // FIXED: Generate unique request ID to prevent overlapping calls
  generateRequestId(prompt, maxTokens, isSummaryRequest = false) {
    const timestamp = Date.now();
    const shortPrompt = prompt.substring(0, 50);
    const requestType = isSummaryRequest ? 'summary' : 'general';
    return `${requestType}_${shortPrompt}_${maxTokens}_${timestamp}`;
  }
  
  // FIXED: More specific cache key that includes timestamp and conversation length
  generateCacheKey(prompt, maxTokens, conversationId = null, timestamp = null) {
    const shortPrompt = prompt.length > 100 ? prompt.substring(0, 100) : prompt;
    const timeComponent = timestamp || Date.now();
    const conversationComponent = conversationId || 'default';
    return `${shortPrompt}_${maxTokens}_${conversationComponent}_${timeComponent}`;
  }
  
  // FIXED: Check if request is cached but NEVER cache summary requests
  getCachedResponse(cacheKey, isSummaryRequest = false) {
    // Never use cache for summary generation
    if (isSummaryRequest) {
      return null;
    }
    
    const cached = this.requestCache.get(cacheKey);
    if (cached && (Date.now() - cached.timestamp) < this.cacheTimeout) {
      return cached.response;
    }
    return null;
  }
  
  // FIXED: Don't cache summary responses
  setCachedResponse(cacheKey, response, isSummaryRequest = false) {
    if (isSummaryRequest) {
      console.log('🚫 Skipping cache for summary request');
      return;
    }
    
    this.requestCache.set(cacheKey, {
      response,
      timestamp: Date.now()
    });
    
    // Clean old cache entries
    if (this.requestCache.size > 50) {
      const entries = Array.from(this.requestCache.entries());
      entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
      // Remove oldest 10 entries
      for (let i = 0; i < 10; i++) {
        this.requestCache.delete(entries[i][0]);
      }
    }
  }
  
  // FIXED: Check if similar request is already in progress
  isRequestInProgress(requestId) {
    return this.activeRequests.has(requestId);
  }
  
  // FIXED: Mark request as active
  markRequestActive(requestId, promise) {
    this.activeRequests.set(requestId, {
      promise,
      timestamp: Date.now()
    });
    
    // Clean up when promise resolves/rejects
    promise.finally(() => {
      this.activeRequests.delete(requestId);
    });
    
    // Set timeout cleanup
    setTimeout(() => {
      if (this.activeRequests.has(requestId)) {
        console.warn(`⚠️ Request ${requestId} timed out, cleaning up`);
        this.activeRequests.delete(requestId);
      }
    }, this.requestTimeout);
  }
  
  // FIXED: Clean up expired active requests
  cleanupExpiredRequests() {
    const now = Date.now();
    const expiredRequests = [];
    
    for (const [requestId, requestData] of this.activeRequests) {
      if (now - requestData.timestamp > this.requestTimeout) {
        expiredRequests.push(requestId);
      }
    }
    
    expiredRequests.forEach(requestId => {
      console.warn(`🧹 Cleaning up expired request: ${requestId}`);
      this.activeRequests.delete(requestId);
    });
  }
  
  // Rate-limited request processing with duplicate prevention
  async processRequest(requestFn, requestId = null) {
    return new Promise((resolve, reject) => {
      this.requestQueue.push({ requestFn, resolve, reject, requestId });
      this.processQueue();
    });
  }
  
  async processQueue() {
    if (this.isProcessingQueue || this.requestQueue.length === 0) {
      return;
    }
    
    this.isProcessingQueue = true;
    
    while (this.requestQueue.length > 0) {
      const { requestFn, resolve, reject, requestId } = this.requestQueue.shift();
      
      try {
        // FIXED: Skip if duplicate request is already in progress
        if (requestId && this.isRequestInProgress(requestId)) {
          console.log(`⏭️ Skipping duplicate request: ${requestId}`);
          // Wait for existing request instead
          const existingRequest = this.activeRequests.get(requestId);
          if (existingRequest) {
            try {
              const result = await existingRequest.promise;
              resolve(result);
              continue;
            } catch (error) {
              reject(error);
              continue;
            }
          }
        }
        
        // Ensure minimum interval between requests
        const timeSinceLastRequest = Date.now() - this.lastRequestTime;
        if (timeSinceLastRequest < this.minRequestInterval) {
          await new Promise(res => setTimeout(res, this.minRequestInterval - timeSinceLastRequest));
        }
        
        const result = await requestFn();
        this.lastRequestTime = Date.now();
        resolve(result);
        
      } catch (error) {
        reject(error);
      }
      
      // Small delay between queue items
      await new Promise(res => setTimeout(res, 100));
    }
    
    this.isProcessingQueue = false;
  }

  // FIXED: Enhanced generateResponse with duplicate request prevention
  async generateResponse(prompt, maxTokens = 1000, retryCount = 3, isSummaryRequest = false) {
    // Clean up expired requests first
    this.cleanupExpiredRequests();
    
    // Generate unique request ID
    const requestId = this.generateRequestId(prompt, maxTokens, isSummaryRequest);
    
    // FIXED: Check if identical request is already in progress
    if (isSummaryRequest && this.isRequestInProgress(requestId)) {
      console.log(`🔄 Summary request already in progress, waiting...`);
      const existingRequest = this.activeRequests.get(requestId);
      if (existingRequest) {
        return await existingRequest.promise;
      }
    }
    
    // FIXED: Never use cache for summary requests
    const conversationId = isSummaryRequest ? `summary_${Date.now()}` : null;
    const cacheKey = this.generateCacheKey(prompt, maxTokens, conversationId, Date.now());
    const cachedResponse = this.getCachedResponse(cacheKey, isSummaryRequest);
    
    if (cachedResponse && !isSummaryRequest) {
      console.log('🎯 Using cached response');
      return cachedResponse;
    }
    
    // Create the actual API request function
    const apiRequestFn = async () => {
      const apiUrl = `${this.endpoint}/openai/deployments/${this.deployment}/chat/completions?api-version=2023-12-01-preview`;

      for (let attempt = 0; attempt < retryCount; attempt++) {
        try {
          const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'api-key': this.apiKey,
            },
            body: JSON.stringify({
              messages: [
                {
                  role: 'system',
                  content: "You are a journaling assistant. Based on the conversation below, write a brief journal summary in the first person. Focus on the user's emotional journey or personal reflections. Never mention 'AI companion' or 'AI' or 'Assistant' - replace those words with 'Preptune'. Just say Preptune instead when you notice user is speaking to an AI.",
                },
                {
                  role: 'user',
                  content: prompt,
                },
              ],
              max_tokens: maxTokens,
              temperature: 0.7,
              top_p: 0.95,
              frequency_penalty: 0,
              presence_penalty: 0,
            }),
          });

          if (!response.ok) {
            if (response.status === 429 && attempt < retryCount - 1) {
              const retryAfter = Math.min(2000 * Math.pow(2, attempt), 10000);
              console.warn(`Rate limit hit. Retrying in ${retryAfter / 1000}s...`);
              await new Promise((res) => setTimeout(res, retryAfter));
              continue;
            }

            const errorData = await response.json();
            throw new Error(
              `Azure OpenAI API error: ${response.status} - ${errorData.error?.message || 'Unknown error'}`
            );
          }

          const data = await response.json();
          const result = data.choices[0]?.message?.content || 'No response generated';
          
          // FIXED: Don't cache summary responses
          this.setCachedResponse(cacheKey, result, isSummaryRequest);
          
          if (isSummaryRequest) {
            console.log('✅ Generated fresh summary (not cached)');
          }
          
          return result;

        } catch (error) {
          if (attempt === retryCount - 1) {
            console.error('Azure OpenAI final error:', error);
            throw new Error(`Failed to generate response: ${error.message}`);
          }
          
          // Wait before retry
          await new Promise(res => setTimeout(res, 1000 * (attempt + 1)));
        }
      }
    };
    
    // FIXED: Process through rate-limited queue with request tracking
    const requestPromise = this.processRequest(apiRequestFn, requestId);
    
    // FIXED: Mark request as active to prevent duplicates
    if (isSummaryRequest) {
      this.markRequestActive(requestId, requestPromise);
    }
    
    return requestPromise;
  }
  
  // FIXED: Enhanced summarizeConversation with better duplicate prevention
  async summarizeConversation(conversation, userId = null, previousSessions = []) {
    if (!conversation || conversation.length === 0) {
      throw new Error('No conversation data provided');
    }
    
    const conversationId = `conv_${Date.now()}_${conversation.length}`;
    console.log(`🔄 Summarizing conversation ${conversationId} with ${conversation.length} messages`);
    
    // Check conversation depth for different handling
    const meaningfulMessages = conversation.filter(msg => 
      msg.type !== 'system' && msg.content && msg.content.trim().length > 10
    );
    
    // Handle truly empty conversations with varied headers
    if (meaningfulMessages.length === 0) {
      console.log('📝 Generating empty conversation summary');
      if (userId && previousSessions.length > 0) {
        const smartHeader = await this.generateSmartEmptyHeader(userId, previousSessions);
        const result = await this.generateEmptyConversationSummary();
        return {
          header: smartHeader,
          content: result.content
        };
      } else {
        return await this.generateEmptyConversationSummary();
      }
    }
    
    // Handle brief conversations (1-2 meaningful messages)
    if (meaningfulMessages.length <= 2) {
      console.log('📝 Generating brief conversation summary');
      return await this.generateBriefSummary(conversation, meaningfulMessages);
    }
    
    console.log('📝 Generating full conversation summary');
    
    // FIXED: Add unique identifiers to ensure fresh summaries
    const conversationHash = this.generateConversationHash(conversation);
    const timestamp = new Date().toISOString();
    
    // Format conversation for better readability with explicit timestamps
    const formattedConversation = conversation
      .filter(msg => msg.type !== 'system')
      .map((msg, index) => {
        const timestamp = msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString() : `Msg${index}`;
        const speaker = msg.type === 'user' ? 'User' : 'Assistant';
        return `${speaker} [${timestamp}]: ${msg.content}`;
      })
      .join('\n');
    
    // OPTIMIZATION: Single prompt for both header and content with uniqueness markers
    const prompt = `
CONVERSATION ANALYSIS SESSION: ${timestamp}
CONVERSATION HASH: ${conversationHash}
CONVERSATION ID: ${conversationId}
TOTAL MESSAGES: ${conversation.length}

Analyze the following conversation between a user and an AI emotional companion.

First, generate a short, meaningful header (2-5 words) that captures the main theme.
Then, write a journal-style summary as described below.

HEADER EXAMPLES: "Career Growth Journey", "Processing Difficult Emotions", "Family Relationship Insights", "Daily Habits & Routines"

SUMMARY INSTRUCTIONS:
- Write as if it's a journal entry that reflects only what the **user shared**
- Never mention the AI or what the AI said
- Focus on: user's feelings, concerns, thoughts, emotional shifts, meaningful themes
- Use warm, human tone with "you" language
- Keep natural, like a moment of reflection
- 3 to 5 sentences max
- No markdown formatting

FORMAT YOUR RESPONSE EXACTLY LIKE THIS:
HEADER: [Your header here]
SUMMARY: [Your summary here]

STRICT STYLE RULES:
- Use second-person ("you") ONLY. 
  ✅ Examples: "You mentioned...", "You felt...", "You were thinking..."
- DO NOT use first-person at all — NEVER write from the assistant's or user's point of view.
  ❌ No "I", "I'm", "we", "me", "my", "ours", "ourselves", etc.
- The summary should reflect the user's *feelings*, *thoughts*, *concerns*, and *emotional tone* as if they're being gently reflected back to them.
- DO NOT mention Preptune, assistants, AI, or bots — they should not appear in the summary at all
- Write in a warm, human, reflective tone — like a journal entry

Current Conversation (${conversation.length} messages):
${formattedConversation}
`;
    
    return await this.processStructuredResponse(prompt, true, conversationId);
  }
  
  // FIXED: Add conversation hash for uniqueness
  generateConversationHash(conversation) {
    const content = conversation
      .filter(msg => msg.type !== 'system')
      .map(msg => msg.content)
      .join('');
    
    // Simple hash function
    let hash = 0;
    for (let i = 0; i < content.length; i++) {
      const char = content.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(36);
  }
  
  // Generate summary for brief conversations with appropriate headers
  async generateBriefSummary(conversation, meaningfulMessages) {
    const timestamp = new Date().toISOString();
    const conversationHash = this.generateConversationHash(conversation);
    const conversationId = `brief_${Date.now()}_${conversation.length}`;
    
    const formattedConversation = conversation
      .filter(msg => msg.type !== 'system')
      .map((msg, index) => {
        const timestamp = msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString() : `Msg${index}`;
        const speaker = msg.type === 'user' ? 'User' : 'Assistant';
        return `${speaker} [${timestamp}]: ${msg.content}`;
      })
      .join('\n');
    
    const prompt = `
BRIEF CONVERSATION ANALYSIS SESSION: ${timestamp}
CONVERSATION HASH: ${conversationHash}
CONVERSATION ID: ${conversationId}
TOTAL MESSAGES: ${conversation.length} (Brief)

Analyze this brief conversation and create an appropriate header and summary.

Since this is a brief interaction, generate a header that reflects the brevity but still captures what happened:

BRIEF HEADER EXAMPLES: 
- "Quick Check-in"
- "Brief Moment"  
- "Short Reflection"
- "Quick Chat"
- "Passing Thought"
- "Brief Touch Base"
- "Quick Hello"
- "Moment of Connection"
- "Quick Question"
- "Brief Exchange"

STRICT STYLE RULES:
- Use second-person ("you") ONLY. 
  ✅ Examples: "You mentioned...", "You felt...", "You were thinking..."
- DO NOT use first-person at all — NEVER write from the assistant's or user's point of view.
  ❌ No "I", "I'm", "we", "me", "my", "ours", "ourselves", etc.

Choose a header that feels natural for a short interaction.

For the summary, acknowledge this was brief but still capture what the user shared in 1-2 sentences.

FORMAT YOUR RESPONSE EXACTLY LIKE THIS:
HEADER: [Your brief header here]
SUMMARY: [Your brief summary here]

Brief conversation:
${formattedConversation}
`;
    
    return await this.processStructuredResponse(prompt, true, conversationId);
  }
  
  // FIXED: Enhanced helper method to process structured API responses with request tracking
  async processStructuredResponse(prompt, isSummaryRequest = false, conversationId = null) {
    try {
      const response = await this.generateResponse(prompt, 1500, 3, isSummaryRequest);
      
      // Parse the structured response
      const headerMatch = response.match(/HEADER:\s*(.+?)(?:\n|SUMMARY:)/i);
      const summaryMatch = response.match(/SUMMARY:\s*(.+)/is);
      
      const header = headerMatch ? headerMatch[1].trim().replace(/['"]/g, '') : 'Today\'s Reflection';
      const content = summaryMatch ? summaryMatch[1].trim() : response;
      
      console.log(`✅ Generated structured response for ${conversationId || 'unknown'}`);
      
      return {
        header: header.length > 50 ? 'Today\'s Reflection' : header,
        content: content
      };
      
    } catch (error) {
      console.error('Failed to generate summary:', error);
      return {
        header: 'Today\'s Reflection',
        content: 'Unable to generate summary at this time. Please try again later.'
      };
    }
  }
  
  // FIXED: Enhanced methods with request tracking
  async generateSummaryHeader(conversation) {
    if (!conversation || conversation.length === 0) {
      return 'Today\'s Reflection';
    }
    
    const timestamp = new Date().toISOString();
    const conversationHash = this.generateConversationHash(conversation);
    const requestId = `header_${conversationHash}_${timestamp}`;
    
    // Format conversation for analysis
    const formattedConversation = conversation
      .filter(msg => msg.type !== 'system')
      .map(msg => msg.content)
      .join(' ')
      .substring(0, 2000);
    
    const prompt = `
HEADER GENERATION SESSION: ${timestamp}
CONVERSATION HASH: ${conversationHash}
REQUEST ID: ${requestId}

Analyze the following conversation and generate a short, meaningful header (2-5 words) that captures the main theme or topic the user discussed.

Examples of good headers:
- "Career Growth Journey"
- "Processing Difficult Emotions"
- "Family Relationship Insights"
- "Daily Habits & Routines"
- "Creative Project Updates"
- "Health & Wellness Check"
- "Financial Planning Thoughts"
- "Personal Goal Setting"
- "Relationship Communication"
- "Self-Care Reflections"
- "Work-Life Balance"
- "Learning New Skills"
- "Future Planning Session"
- "Overcoming Challenges"
- "Celebrating Small Wins"

Choose a header that feels personal and specific to what the user shared. Avoid generic terms like "Conversation Summary" or "Today's Chat."

Return ONLY the header text, no quotes or additional formatting.

Conversation content:
${formattedConversation}
`;
    
    try {
      const header = await this.generateResponse(prompt, 50, 3, true);
      const cleanHeader = header.trim().replace(/['"]/g, '');
      return cleanHeader.length > 50 ? 'Today\'s Reflection' : cleanHeader;
    } catch (error) {
      console.error('Failed to generate header:', error);
      return 'Today\'s Reflection';
    }
  }
  
  // Keep all other methods unchanged but add request tracking where appropriate...
  async generateEmptyConversationSummary() {
    const emptyHeaders = [
      'Brief Check-in',
      'Quick Stop-by',
      'Passing Through',
      'Just Dropping In',
      'Quick Peek',
      'Brief Visit',
      'Fast Check',
      'Quick Look',
      'Casual Drop-in',
      'Brief Moment',
      'Quick Touch Base',
      'Simple Check',
      'Brief Stop',
      'Quick Catch-up',
      'Light Check-in',
      'Brief Hello',
      'Quick Connection',
      'Casual Check',
      'Short Visit',
      'Brief Touch'
    ];
    
    const emptySummaries = [
      'You stopped by but didn\'t dive into anything specific today.',
      'A quick check-in without getting into the details—sometimes that\'s all you need.',
      'You opened the app but kept things light. No pressure to always have something to say.',
      'Just a brief moment of connection without diving deep.',
      'You took a quick look but didn\'t feel like chatting much today.',
      'A casual drop-in to see how things felt, then moved on with your day.',
      'You checked in briefly but didn\'t need to unpack anything right now.',
      'Sometimes you just want to touch base without a big conversation.',
      'A quick visit to your journal space, keeping things simple today.',
      'You paused here for a moment but didn\'t need to dig into anything heavy.',
      'Just a brief check to see how the space felt today.',
      'You stopped in casually without needing to share much.',
      'A light touch-base moment in your day.',
      'You glanced in but didn\'t feel the need for a deeper chat.',
      'Brief and easy—not every session needs to be intense.'
    ];
    
    const randomHeader = emptyHeaders[Math.floor(Math.random() * emptyHeaders.length)];
    const randomSummary = emptySummaries[Math.floor(Math.random() * emptySummaries.length)];
    
    return {
      header: randomHeader,
      content: randomSummary
    };
  }

  // Time-based empty headers
  async generateTimeBasedEmptyHeader() {
    const now = new Date();
    const hour = now.getHours();
    const dayOfWeek = now.getDay();
    
    let timeBasedHeaders = [];
    
    if (hour >= 6 && hour < 12) {
      timeBasedHeaders = [
        'Morning Check-in',
        'Early Drop-by',
        'Morning Visit',
        'AM Quick Look',
        'Morning Touch Base'
      ];
    } else if (hour >= 12 && hour < 18) {
      timeBasedHeaders = [
        'Lunch Break Visit',
        'Afternoon Check',
        'Midday Stop-by',
        'PM Quick Peek',
        'Afternoon Catch-up'
      ];
    } else if (hour >= 18 && hour < 22) {
      timeBasedHeaders = [
        'Evening Check-in',
        'After Work Visit',
        'Evening Drop-by',
        'Night Quick Look',
        'Evening Touch Base'
      ];
    } else {
      timeBasedHeaders = [
        'Late Night Check',
        'Midnight Visit',
        'Night Drop-by',
        'Late Check-in',
        'Night Quick Peek'
      ];
    }
    
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      timeBasedHeaders.push('Weekend Check-in', 'Casual Weekend Visit', 'Weekend Drop-by');
    }
    
    return timeBasedHeaders[Math.floor(Math.random() * timeBasedHeaders.length)];
  }

  async generateContextualEmptyHeader(userId, previousSessions = []) {
    const recentActiveSessions = previousSessions.filter(session => 
      session.messageCount > 5 && 
      (Date.now() - session.timestamp) < 24 * 60 * 60 * 1000
    );
    
    if (recentActiveSessions.length > 0) {
      const contrastHeaders = [
        'Quick Break',
        'Simple Check',
        'Light Visit',
        'Easy Drop-by',
        'Casual Peek'
      ];
      return contrastHeaders[Math.floor(Math.random() * contrastHeaders.length)];
    }
    
    const recentQuietSessions = previousSessions.filter(session => 
      session.messageCount <= 2 && 
      (Date.now() - session.timestamp) < 7 * 24 * 60 * 60 * 1000
    );
    
    if (recentQuietSessions.length > 3) {
      const regularHeaders = [
        'Regular Check-in',
        'Usual Visit',
        'Routine Stop',
        'Standard Drop-by',
        'Normal Check'
      ];
      return regularHeaders[Math.floor(Math.random() * regularHeaders.length)];
    }
    
    const result = await this.generateEmptyConversationSummary();
    return result.header;
  }

  async generateSmartEmptyHeader(userId, previousSessions = []) {
    const approaches = ['random', 'timeBased', 'contextual'];
    const selectedApproach = approaches[Math.floor(Math.random() * approaches.length)];
    
    switch (selectedApproach) {
      case 'timeBased':
        return await this.generateTimeBasedEmptyHeader();
      case 'contextual':
        return await this.generateContextualEmptyHeader(userId, previousSessions);
      default:
        const result = await this.generateEmptyConversationSummary();
        return result.header;
    }
  }
  
  async generateEmotionalInsights(conversationData) {
    if (!conversationData || conversationData.length === 0) {
      throw new Error('No conversation data provided for emotional insights');
    }
    
    const emotionData = conversationData
      .filter(msg => msg.emotions && msg.emotions.length > 0)
      .map(msg => ({
        content: msg.content,
        emotions: msg.emotions,
        timestamp: msg.timestamp
      }));
    
    if (emotionData.length === 0) {
      console.log('No emotional data found, skipping insights generation');
      return '';
    }
    
    const conversationText = conversationData
      .filter(msg => msg.type !== 'system')
      .map(msg => `${msg.type}: ${msg.content}`)
      .join('\n');
    
    const prompt = `
Analyze the following conversation and emotional data to generate a personal, human-style journal entry that helps the user reflect on their emotional state and communication patterns.

Structure the output like a journal entry, not a technical report.

Use the following format:

---

**Today's Journal Entry**

Start with a brief description of the day's emotional flow. Describe shifts in mood, energy, or tone that stood out.

Mention any highs or lows — moments where the user felt especially strong emotions (positive or negative).

Note any emotional patterns that repeated — recurring thoughts, reactions, or emotional triggers.

Close with a supportive reflection, like something the user might write to themselves at the end of the day. Keep the tone warm, self-aware, and constructive.

---

Conversation emotion signals:
${JSON.stringify(emotionData, null, 2)}

Full conversation:
${conversationText}

Avoid referencing systems, AI, or technical tools. Focus only on the user's feelings, reflections, and personal growth.
`;

    try {
      return await this.generateResponse(prompt, 1500, 3, true);
    } catch (error) {
      console.error('Failed to generate emotional insights:', error);
      return '';
    }
  }
  
  async generatePersonalSummary(conversationData) {
    if (!conversationData || conversationData.length === 0) {
      throw new Error('No conversation data provided for personal summary');
    }
    
    const conversationText = conversationData
      .filter(msg => msg.type !== 'system')
      .map(msg => `${msg.type}: ${msg.content}`)
      .join('\n');
    
    const prompt = `
Based on this conversation, generate a personal summary in JSON format with the following structure:
{
  "narrative": "A brief personal narrative of what happened in the conversation",
  "themes": ["array", "of", "main", "themes"],
  "actions": ["array", "of", "actions", "or", "strategies", "discussed"],
  "tone": "description of the overall emotional tone progression"
}

STRICT STYLE RULES:
- Use second-person ("you") ONLY. 
  ✅ Examples: "You mentioned...", "You felt...", "You were thinking..."
- DO NOT use first-person at all — NEVER write from the assistant's or user's point of view.
  ❌ No "I", "I'm", "we", "me", "my", "ours", "ourselves", etc.
- The summary should reflect the user's *feelings*, *thoughts*, *concerns*, and *emotional tone* as if they're being gently reflected back to them.
- DO NOT mention Preptune, assistants, AI, or bots — they should not appear in the summary at all
- Write in a warm, human, reflective tone — like a journal entry

Conversation:
${conversationText}

Write the narrative as if you're gently reflecting back to the user in a compassionate tone. Focus only on what the user expressed — their feelings, thoughts, and intentions. Avoid referencing AI, systems, or tools.

The output should feel warm and insightful, like a kind mirror showing the user's emotional journey.

Return only valid JSON — no extra explanation or formatting.
`;

    try {
      const response = await this.generateResponse(prompt, 800, 3, true);
      return JSON.parse(response);
    } catch (error) {
      console.error('Failed to parse personal summary JSON:', error);
      return {
        narrative: "The conversation covered important personal topics and emotional themes.",
        themes: ["personal reflection", "emotional wellbeing"],
        actions: ["continue self-reflection", "practice mindfulness"],
        tone: "thoughtful and introspective"
      };
    }
  }
  
  // FIXED: Clear cache method specifically for summaries and active requests
  clearSummaryCache() {
    const summaryKeys = Array.from(this.requestCache.keys()).filter(key => key.includes('summary_'));
    summaryKeys.forEach(key => this.requestCache.delete(key));
    
    // Also clear active summary requests
    const activeSummaryKeys = Array.from(this.activeRequests.keys()).filter(key => key.startsWith('summary_'));
    activeSummaryKeys.forEach(key => this.activeRequests.delete(key));
    
    console.log(`🧹 Cleared ${summaryKeys.length} summary cache entries and ${activeSummaryKeys.length} active summary requests`);
  }
  
  // FIXED: Enhanced cache clearing with active request cleanup
  clearCache() {
    this.requestCache.clear();
    this.activeRequests.clear();
    console.log('🧹 Request cache and active requests cleared');
  }
  
  // FIXED: Enhanced cache statistics including active requests
  getCacheStats() {
    return {
      cacheSize: this.requestCache.size,
      activeRequests: this.activeRequests.size,
      cacheEntries: Array.from(this.requestCache.keys()),
      activeRequestIds: Array.from(this.activeRequests.keys())
    };
  }
  
  // FIXED: Method to cancel all active requests (useful for preventing overlaps)
  cancelAllActiveRequests() {
    console.log(`🚫 Cancelling ${this.activeRequests.size} active requests`);
    this.activeRequests.clear();
    this.requestQueue.length = 0; // Clear pending queue
  }
  
  // FIXED: Method to check if any summary requests are active
  hasSummaryRequestsActive() {
    const activeSummaryKeys = Array.from(this.activeRequests.keys()).filter(key => 
      key.startsWith('summary_') || key.startsWith('brief_') || key.startsWith('header_')
    );
    return activeSummaryKeys.length > 0;
  }
}

export default AzureOpenAIService;